import i18n from 'i18next'
import _ from 'lodash'
import * as Yup from 'yup'
import { removeEmpyObjectValues } from '../../../utils/array'
import { addPercentageSuffix, convertCleaveFormat } from '../../../utils/validations'
import { REAL_ESTATE_TYPES } from '../../constants'
import responseFields from '../../constants/responseFields'
import { contactResponseFields } from '../../response/contact'
import { contactDetailSearchFields, contactGeneralFields, contactNoteFields, contactOtherInfoFields, contactProfileFields } from './fields'

export const contactGeneralValidation = (data) => {
    return {
        initialValues: {
            [contactGeneralFields.ID]: data?.internalAppId ?? '', // internalAppId is used for display, id is sent in prepareModelForSubmit
            [contactGeneralFields.CONTACT_TITLE_ID]: data?.[contactResponseFields.CONTACT_PERSON]?.[contactResponseFields.CONTACT_TITLE]?.id ?? '',
            [contactGeneralFields.FIRST_NAME]: data?.[contactResponseFields.CONTACT_PERSON]?.firstName ?? '',
            [contactGeneralFields.LAST_NAME]: data?.[contactResponseFields.CONTACT_PERSON]?.lastName ?? '',
            [contactGeneralFields.INITIAL_CONTACT_DATE]: data?.initialContactDate ?? '',
            [contactGeneralFields.CONTACT_PRIORITY_ID]: data?.contactPriority?.id ?? '',
            [contactGeneralFields.DATA_SOURCE_ID]: data?.dataSource?.id ?? '',
            contact_phone_group: data?.contactPhone?.length
                ? data?.contactPhone?.map((item) => ({
                      [contactGeneralFields.PHONE_NUMBER]: item?.phoneNumber ?? '',
                      [contactGeneralFields.CONTACT_PHONE_TYPE_ID]: item?.contactPhoneType?.id ?? '',
                  }))
                : [
                      {
                          [contactGeneralFields.PHONE_NUMBER]: '',
                          [contactGeneralFields.CONTACT_PHONE_TYPE_ID]: '',
                      },
                  ],
            contact_email_group: data?.contactEmail?.length
                ? data?.contactEmail?.map((item) => ({
                      [contactGeneralFields.EMAIL]: item?.email ?? '',
                      [contactGeneralFields.CONTACT_EMAIL_TYPE_ID]: item?.contactEmailType?.id ?? '',
                  }))
                : [
                      {
                          [contactGeneralFields.EMAIL]: '',
                          [contactGeneralFields.CONTACT_EMAIL_TYPE_ID]: '',
                      },
                  ],
            [contactGeneralFields.STREET]: data?.[contactResponseFields.STREET] ?? '',
            [contactGeneralFields.STREET_NUMBER]: data?.[contactResponseFields.STREET_NUMBER] ?? '',
            [contactGeneralFields.CONTACT_COUNTRY_ID]: data?.contactCountry?.id?.toString() ?? '',
            [contactGeneralFields.CITY]: data?.city ?? '',
            [contactGeneralFields.ZIP_CODE]: data?.zipCode ?? '',
            [contactGeneralFields.CONTACT_STATUS_ID]: data?.[contactResponseFields.CONTACT_STATUS]?.id?.toString() ?? '',
            [contactGeneralFields.AGENT_ID]: data?.agent?.id ?? '',
            [contactGeneralFields.CONTACT_QUERY_CATEGORY_ID]: data?.contactQueryCategory?.id ?? '',
        },
        validationSchema: Yup.object({
            [contactGeneralFields.CONTACT_TITLE_ID]: Yup.number(),
            [contactGeneralFields.FIRST_NAME]: Yup.string().required(i18n.t('validation.message')),
            [contactGeneralFields.LAST_NAME]: Yup.string().required(i18n.t('validation.message')),
            [contactGeneralFields.INITIAL_CONTACT_DATE]: Yup.string(),
            [contactGeneralFields.CONTACT_PRIORITY_ID]: Yup.number(),
            [contactGeneralFields.DATA_SOURCE_ID]: Yup.number(),
            [contactGeneralFields.CONTACT_STATUS_ID]: Yup.string().required(i18n.t('validation.message')),
            contact_phone_group: Yup.array().of(
                Yup.object().shape({
                    [contactGeneralFields.PHONE_NUMBER]: Yup.string().matches('^[0-9\\+\\ ]*$', i18n.t('validation.phone')),
                    [contactGeneralFields.CONTACT_PHONE_TYPE_ID]: Yup.number(),
                })
            ),
            contact_email_group: Yup.array().of(
                Yup.object().shape({
                    [contactGeneralFields.EMAIL]: Yup.string().email(i18n.t('validation.email')),
                    [contactGeneralFields.CONTACT_EMAIL_TYPE_ID]: Yup.number(),
                })
            ),
            [contactGeneralFields.STREET]: Yup.string(),
            [contactGeneralFields.STREET_NUMBER]: Yup.string(),
            [contactGeneralFields.CITY]: Yup.string(),
            [contactGeneralFields.ZIP_CODE]: Yup.string(),
            [contactGeneralFields.AGENT_ID]: Yup.string().required(i18n.t('validation.message')),
        }),
        prepareModelForSubmit: (values) => {
            return {
                ...values,
                [contactGeneralFields.ID]: data?.id ?? '',
                contact_email_group: removeEmpyObjectValues(values.contact_email_group),
                contact_phone_group: removeEmpyObjectValues(values.contact_phone_group),
            }
        },
    }
}

export const contactOtherInfoValidation = (data) => {
    return {
        initialValues: {
            [contactOtherInfoFields.CONTACT_DESCRIPTION_ID]: data?.contactDescription?.id ?? '',
            [contactOtherInfoFields.CONTACT_GENDER_ID]: data?.[contactResponseFields.CONTACT_PERSON]?.contactGender?.id ?? '',
            [contactOtherInfoFields.OIB]: data?.taxNumber ?? '',
            contact_person_company_group: data?.contactPersonCompany?.length
                ? data?.contactPersonCompany?.map((item) => ({
                      [contactOtherInfoFields.COMPANY_ID]: item.company?.id?.toString() ?? '',
                      [contactOtherInfoFields.CONTACT_JOB_TITLE_ID]: item.contactJobTitle?.id.toString() ?? '',
                      [contactOtherInfoFields.OWNERSHIP_PERCENTAGE]: item.ownershipPercentage ?? '',
                      [contactOtherInfoFields.EMPLOYMENT_TYPE]: item.employmentType ?? '',
                  }))
                : [
                      {
                          [contactOtherInfoFields.COMPANY_ID]: '',
                          [contactOtherInfoFields.CONTACT_JOB_TITLE_ID]: '',
                          [contactOtherInfoFields.OWNERSHIP_PERCENTAGE]: '',
                          [contactOtherInfoFields.EMPLOYMENT_TYPE]: '',
                      },
                  ],
            [contactOtherInfoFields.BIRTH_COUNTRY_ID]: data?.contactPerson?.birthCountry?.id ?? '',
            [contactOtherInfoFields.BIRTH_PLACE]: data?.contactPerson?.birthPlace ?? '',
            [contactOtherInfoFields.BIRTH_DATE]: data?.contactPerson?.birthDate ?? '',
            [contactOtherInfoFields.CONTACT_GENDER_ID]:
                data?.[contactResponseFields.CONTACT_PERSON]?.[contactResponseFields.CONTACT_GENDER]?.id ?? '',
            [contactOtherInfoFields.CONTACT_MARTIAL_STATUS_ID]: data?.contactPerson?.contactMaritalStatus?.id ?? '',
            [contactOtherInfoFields.NOTES]: data?.notes ?? '',
        },
        validationSchema: Yup.object({
            [contactOtherInfoFields.CONTACT_DESCRIPTION_ID]: Yup.number(),
            [contactOtherInfoFields.CONTACT_GENDER_ID]: Yup.number(),
            [contactOtherInfoFields.OIB]: Yup.string(),
            [contactOtherInfoFields.BIRTH_PLACE]: Yup.string(),
            [contactOtherInfoFields.BIRTH_DATE]: Yup.string(),
            [contactOtherInfoFields.CONTACT_MARTIAL_STATUS_ID]: Yup.number(),
            [contactOtherInfoFields.NOTES]: Yup.string(),
            contact_person_company_group: Yup.array().of(
                Yup.object().shape({
                    [contactOtherInfoFields.OWNERSHIP_PERCENTAGE]: Yup.number()
                        .transform(convertCleaveFormat)
                        .nullable()
                        .typeError(i18n.t('validation.number'))
                        .min(1, i18n.t('validation.percentage', { min: 1 }))
                        .max(100, i18n.t('validation.percentage', { max: 100 })),
                    [contactOtherInfoFields.EMPLOYMENT_TYPE]: Yup.string(),
                })
            ),
        }),
        prepareModelForSubmit: (values) => {
            // process ownership_percentage field to ensure it has % suffix
            const processedCompanyGroup = values.contact_person_company_group?.map((company) => ({
                ...company,
                ownership_percentage: addPercentageSuffix(company.ownership_percentage),
            }))

            return {
                ...values,
                contact_person_company_group: removeEmpyObjectValues(processedCompanyGroup),
            }
        },
    }
}

export const contactProfileValidation = (data, propertyTypeValue, featureFlags) => {
    const propertyType = Number(propertyTypeValue)
    const { house, flat, officeSpace, land, garage } = REAL_ESTATE_TYPES

    const isHouse = propertyType === house
    const isFlat = propertyType === flat
    const isOffice = propertyType === officeSpace
    const isLand = propertyType === land
    const isGarage = propertyType === garage
    const isRooms = [house, flat, officeSpace].includes(propertyType)
    const isBedrooms = [house, flat].includes(propertyType)

    return {
        initialValues: {
            id: data?.id ?? '',
            [contactProfileFields.CONTACT_PROFILE_SERVICE_ID]: data?.[contactResponseFields.CONTACT_PROFILE_SERVICE]?.id ?? '',
            [contactProfileFields.PROPERTY_TYPE_ID]: data?.propertyType?.id ?? '',
            [contactProfileFields.PROPERTY_SUB_TYPE_ID]: data?.propertySubType?.map((item) => item.id?.toString()) ?? [],
            [contactProfileFields.CONTACT_PROFILE_STATUS_ID]: data?.[contactResponseFields.CONTACT_PROFILE_STATUS]?.id?.toString() ?? '',
            [contactProfileFields.CONTACT_FINANCING_ID]: data?.[contactResponseFields.CONTACT_FINANCING]?.map((item) => item.id?.toString()) ?? [],
            [contactProfileFields.CONTACT_PROFILE_DURATION_ID]: data?.[contactResponseFields.CONTACT_PROFILE_DURATION]?.id?.toString() ?? '',
            [contactProfileFields.CONTACT_DEFINITION_ID]: data?.[contactResponseFields.CONTACT_DEFINITION]?.id?.toString() ?? '',
            [contactProfileFields.COUNTRY_ID]: data?.[contactResponseFields.COUNTRY]?.id?.toString() ?? '',
            [contactProfileFields.COUNTY_ID]: data?.[contactResponseFields.COUNTY]?.id?.toString() ?? '',
            [contactProfileFields.WIDER_AREA_ID]: data?.[contactResponseFields.WIDER_AREA]?.map((item) => item.id?.toString()) ?? [],
            [contactProfileFields.CITY_ID]: data?.[contactResponseFields.CITY]?.map((item) => item.id?.toString()) ?? [],
            [contactProfileFields.QUARTER_ID]: data?.[contactResponseFields.QUARTER]?.map((item) => item.id?.toString()) ?? [],
            [contactProfileFields.ISLAND_ID]: data?.[contactResponseFields.ISLAND]?.map((item) => item.id?.toString()) ?? [],
            [contactProfileFields.PRICE_FROM]: data?.[contactResponseFields.PRICE_FROM] ?? '',
            [contactProfileFields.PRICE_TO]: data?.[contactResponseFields.PRICE_TO] ?? '',
            ...(isRooms
                ? {
                      [contactProfileFields.ROOMS_FROM]: data?.[contactResponseFields.ROOMS_FROM] ?? '',
                      [contactProfileFields.ROOMS_TO]: data?.[contactResponseFields.ROOMS_TO] ?? '',
                  }
                : {}),
            ...(isBedrooms
                ? {
                      [contactProfileFields.BEDROOMS_FROM]: data?.[contactResponseFields.BEDROOMS_FROM] ?? '',
                      [contactProfileFields.BEDROOMS_TO]: data?.[contactResponseFields.BEDROOMS_TO] ?? '',
                  }
                : {}),
            [contactProfileFields.SPECIAL_REQUEST]: data?.[contactResponseFields.SPECIAL_REQUEST] ?? '',
            [contactProfileFields.AREA_FROM]: data?.[contactResponseFields.AREA_FROM] ?? '',
            [contactProfileFields.AREA_TO]: data?.[contactResponseFields.AREA_TO] ?? '',
            [contactProfileFields.CONSTRUCTION_YEAR_FROM]: data?.[contactResponseFields.CONSTRUCTION_YEAR_FROM] ?? '',
            [contactProfileFields.CONSTRUCTION_YEAR_TO]: data?.[contactResponseFields.CONSTRUCTION_YEAR_TO] ?? '',
            ...(isFlat || isOffice
                ? {
                      [contactProfileFields.PROPERTY_FLOOR_POSITION_ID]:
                          data?.[contactResponseFields.PROPERTY_FLOOR_POSITION]?.map((item) => item.id?.toString()) ?? [],
                      [contactProfileFields.FLOOR_NO_FROM]: data?.[contactResponseFields.FLOOR_FROM] ?? '',
                      [contactProfileFields.FLOOR_NO_TO]: data?.[contactResponseFields.FLOOR_TO] ?? '',
                  }
                : {}),
            ...(isFlat
                ? {
                      [contactProfileFields.EXCLUDE_BASEMENT]: data?.[contactResponseFields.EXCLUDE_BASEMENT] ?? false,
                      [contactProfileFields.EXCLUDE_LAST_FLOOR]: data?.[contactResponseFields.EXCLUDE_LAST_FLOOR] ?? false,
                  }
                : {}),
            ...(isRooms
                ? {
                      [contactProfileFields.PROPERTY_SPACE_ID]: data?.[contactResponseFields.PROPERTY_SPACE]?.map((x) => x.id?.toString()) ?? [],
                  }
                : {}),
            ...(!isGarage
                ? {
                      [contactProfileFields.PROPERTY_DESCRIPTIONS_ID]:
                          data?.[contactResponseFields.PROPERTY_DESCRIPTIONS]?.map((item) => item.id?.toString()) ?? [],
                      [contactProfileFields.PROPERTY_UTILITY_ID]:
                          data?.[contactResponseFields.PROPERTY_UTILITY]?.map((item) => item.id?.toString()) ?? [],
                  }
                : {}),
            [contactProfileFields.PROPERTY_EQUIPMENT_ID]: data?.[contactResponseFields.PROPERTY_EQUIPMENT]?.map((item) => item.id?.toString()) ?? [],
            ...(!isGarage && !isLand
                ? {
                      [contactProfileFields.PROPERTY_HEATING_ID]:
                          data?.[contactResponseFields.PROPERTY_HEATING]?.map((item) => item.id?.toString()) ?? [],
                  }
                : {}),
            ...(isHouse
                ? {
                      [contactProfileFields.PROPERTY_FLOOR_ID]: data?.propertyFloor?.id ?? '',
                  }
                : {}),
            ...(featureFlags?.automatic_offer_sending
                ? {
                      [contactProfileFields.AUTOMATIC_OFFER_ENABLED]: data?.[contactResponseFields.AUTOMATIC_OFFER_ENABLED] ?? false,
                  }
                : {}),
        },
        validationSchema: Yup.object({
            [contactProfileFields.PROPERTY_TYPE_ID]: Yup.string().required(i18n.t('validation.message')),
            [contactProfileFields.CONTACT_PROFILE_SERVICE_ID]: Yup.string().required(i18n.t('validation.message')),
            [contactProfileFields.CONTACT_PROFILE_STATUS_ID]: Yup.number(),
            [contactProfileFields.CONTACT_FINANCING_ID]: Yup.array().of(Yup.string()),
            [contactProfileFields.CONTACT_PROFILE_DURATION_ID]: Yup.number(),
            [contactProfileFields.CONTACT_DEFINITION_ID]: Yup.number(),
            [contactProfileFields.COUNTRY_ID]: Yup.number(),
            [contactProfileFields.PRICE_FROM]: Yup.number().transform(convertCleaveFormat),
            [contactProfileFields.PRICE_TO]: Yup.number()
                .transform(convertCleaveFormat)
                .when(contactProfileFields.PRICE_FROM, {
                    is: (val) => val != null && val !== '',
                    then: (schema) => schema.moreThan(Yup.ref(contactProfileFields.PRICE_FROM), i18n.t('validation.moreThan')),
                }),
            [contactProfileFields.AREA_FROM]: Yup.number().transform(convertCleaveFormat),
            [contactProfileFields.AREA_TO]: Yup.number()
                .transform(convertCleaveFormat)
                .when(contactProfileFields.AREA_FROM, {
                    is: (val) => val != null && val !== '',
                    then: (schema) => schema.moreThan(Yup.ref(contactProfileFields.AREA_FROM), i18n.t('validation.moreThan')),
                }),
            [contactProfileFields.ROOMS_FROM]: Yup.number(),
            [contactProfileFields.ROOMS_TO]: Yup.number().when(contactProfileFields.ROOMS_FROM, {
                is: (val) => val != null && val !== '',
                then: (schema) => schema.moreThan(Yup.ref(contactProfileFields.ROOMS_FROM), i18n.t('validation.moreThan')),
            }),
            [contactProfileFields.BEDROOMS_FROM]: Yup.number(),
            [contactProfileFields.BEDROOMS_TO]: Yup.number().when(contactProfileFields.BEDROOMS_FROM, {
                is: (val) => val != null && val !== '',
                then: (schema) => schema.moreThan(Yup.ref(contactProfileFields.BEDROOMS_FROM), i18n.t('validation.moreThan')),
            }),
            [contactProfileFields.FLOOR_NO_FROM]: Yup.number(),
            [contactProfileFields.FLOOR_NO_TO]: Yup.number().when(contactProfileFields.FLOOR_NO_FROM, {
                is: (val) => val != null && val !== '',
                then: (schema) => schema.moreThan(Yup.ref(contactProfileFields.FLOOR_NO_FROM), i18n.t('validation.moreThan')),
            }),
            [contactProfileFields.CONSTRUCTION_YEAR_FROM]: Yup.number(),
            [contactProfileFields.CONSTRUCTION_YEAR_TO]: Yup.number().when(contactProfileFields.CONSTRUCTION_YEAR_FROM, {
                is: (val) => val != null && val !== '',
                then: (schema) => schema.min(Yup.ref(contactProfileFields.CONSTRUCTION_YEAR_FROM), i18n.t('validation.moreThan')),
            }),
        }),
        prepareModelForSubmit: (values) => {
            const { [contactProfileFields.AUTOMATIC_OFFER_ENABLED]: automaticOfferEnabled, ...rest } = values

            return {
                ...rest,
                ...(featureFlags?.automatic_offer_sending ? { [contactProfileFields.AUTOMATIC_OFFER_ENABLED]: Boolean(automaticOfferEnabled) } : {}),
                [contactProfileFields.PRICE_FROM]: values?.[contactProfileFields.PRICE_FROM]?.toString().replace(/\./g, '').replace(/,/g, '.'),
                [contactProfileFields.PRICE_TO]: values?.[contactProfileFields.PRICE_TO]?.toString().replace(/\./g, '').replace(/,/g, '.'),
                [contactProfileFields.AREA_FROM]: values?.[contactProfileFields.AREA_FROM]?.toString().replace(/\./g, '').replace(/,/g, '.'),
                [contactProfileFields.AREA_TO]: values?.[contactProfileFields.AREA_TO]?.toString().replace(/\./g, '').replace(/,/g, '.'),
            }
        },
    }
}

export const contactNoteValidation = (data) => {
    return {
        initialValues: {
            [contactNoteFields.NOTE_TITLE]: data?.noteTitle ?? '',
            [contactNoteFields.NOTE_DATE]: data?.noteDate ?? '',
            [contactNoteFields.NOTE]: data?.note ?? '',
            [contactNoteFields.CONTACT_NOTE_AGENT_ID]: data?.agent?.id ?? '',
        },
        validationSchema: Yup.object({
            [contactNoteFields.NOTE_TITLE]: Yup.string(),
            [contactNoteFields.NOTE_DATE]: Yup.string(),
            [contactNoteFields.NOTE]: Yup.string(),
            [contactNoteFields.CONTACT_NOTE_AGENT_ID]: Yup.string(),
        }),
    }
}

export const contactDetailSearchValidation = (data) => {
    return {
        initialValues: {
            [contactDetailSearchFields.NAME]: data?.[contactDetailSearchFields.NAME] ?? '',
            [contactDetailSearchFields.CONTACT_PHONE]: data?.[contactDetailSearchFields.CONTACT_PHONE] ?? '',
            [contactDetailSearchFields.CONTACT_EMAIL]: data?.[contactDetailSearchFields.CONTACT_EMAIL] ?? '',
            [contactDetailSearchFields.AGENT]: data?.[contactDetailSearchFields.AGENT] ?? '',
            [contactDetailSearchFields.INITIAL_CONTACT_DATE_FROM]: data?.[contactDetailSearchFields.INITIAL_CONTACT_DATE_FROM] ?? '',
            [contactDetailSearchFields.INITIAL_CONTACT_DATE_TO]: data?.[contactDetailSearchFields.INITIAL_CONTACT_DATE_TO] ?? '',
            [contactDetailSearchFields.LAST_ACTIVITY_DATE_FROM]: data?.[contactDetailSearchFields.LAST_ACTIVITY_DATE_FROM] ?? '',
            [contactDetailSearchFields.LAST_ACTIVITY_DATE_TO]: data?.[contactDetailSearchFields.LAST_ACTIVITY_DATE_TO] ?? '',
            [contactDetailSearchFields.CONTACT_COUNTRY]: data?.[contactDetailSearchFields.CONTACT_COUNTRY] ?? '',
            [contactDetailSearchFields.CITY]: data?.[contactDetailSearchFields.CITY] ?? [],
            [contactDetailSearchFields.CONTACT_PROFILE_SERVICE]: data?.contactProfile?.[contactDetailSearchFields.CONTACT_PROFILE_SERVICE] ?? '',
            [contactDetailSearchFields.CONTACT_PROFILE_STATUS]: data?.contactProfile?.[contactDetailSearchFields.CONTACT_PROFILE_STATUS] ?? '',
            [contactDetailSearchFields.CONTACT_QUERY_CATEGORY]: data?.[contactDetailSearchFields.CONTACT_QUERY_CATEGORY] ?? '',
            [contactDetailSearchFields.DATA_SOURCE]: data?.[contactDetailSearchFields.DATA_SOURCE] ?? '',
            [contactDetailSearchFields.CONTACT_PRIORITY]: data?.[contactDetailSearchFields.CONTACT_PRIORITY] ?? '',
            [contactDetailSearchFields.CONTACT_STATUS]: data?.[contactDetailSearchFields.CONTACT_STATUS],
        },
        formGroup: 'contact',
    }
}

export const contactProfileDetailSearchValidation = (data) => {
    return {
        initialValues: {
            [responseFields.PROPERTY_TYPE]: data?.contactProfile?.[responseFields.PROPERTY_TYPE] ?? [],
            [responseFields.PROPERTY_SUB_TYPE]: data?.contactProfile?.[responseFields.PROPERTY_SUB_TYPE] ?? [],
            [responseFields.PRICE_FROM]: data?.contactProfile?.[responseFields.PRICE_FROM] ?? '',
            [responseFields.PRICE_TO]: data?.contactProfile?.[responseFields.PRICE_TO] ?? '',
            [responseFields.ROOMS_FROM]: data?.contactProfile?.[responseFields.ROOMS_FROM] ?? '',
            [responseFields.ROOMS_TO]: data?.contactProfile?.[responseFields.ROOMS_TO] ?? '',
            [responseFields.BEDROOMS_FROM]: data?.contactProfile?.[responseFields.BEDROOMS_FROM] ?? '',
            [responseFields.BEDROOMS_TO]: data?.contactProfile?.[responseFields.BEDROOMS_TO] ?? '',
            [responseFields.AREA_FROM]: data?.contactProfile?.[responseFields.AREA_FROM] ?? '',
            [responseFields.AREA_TO]: data?.contactProfile?.[responseFields.AREA_TO] ?? '',
            [responseFields.FLOOR_FROM]: data?.contactProfile?.[responseFields.FLOOR_FROM] ?? '',
            [responseFields.FLOOR_TO]: data?.contactProfile?.[responseFields.FLOOR_TO] ?? '',
            [responseFields.CONTACT_PROFILE_DURATION]: data?.contactProfile?.[responseFields.CONTACT_PROFILE_DURATION] ?? '',
            [responseFields.COUNTRY]: data?.contactProfile?.[responseFields.COUNTRY] ?? '',
            [responseFields.COUNTY]: data?.contactProfile?.[responseFields.COUNTY] ?? [],
            [responseFields.CITY]: data?.contactProfile?.[responseFields.CITY] ?? [],
            [responseFields.WIDER_AREA]: data?.contactProfile?.[responseFields.WIDER_AREA] ?? [],
            [responseFields.QUARTER]: data?.contactProfile?.[responseFields.QUARTER] ?? [],
            [responseFields.PROPERTY_FLOOR]: data?.contactProfile?.[responseFields.PROPERTY_FLOOR] ?? [],
        },
        formGroup: 'contactProfile',
    }
}
